import { RefObject, useEffect, useRef } from 'react';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import gsap from 'gsap';

type PanelSlide = {
  open: boolean;
  /** Mounted state; the slide-out keeps the panel mounted until it finishes. */
  rendered: boolean;
  panelRef: RefObject<HTMLElement>;
  backdropRef: RefObject<HTMLElement>;
  /** The panel's off-screen position, e.g. `{ x: '100%' }` for a right-hand panel. */
  hidden: gsap.TweenVars;
  /** Called once the panel is fully closed, to unmount it. */
  onClosed: () => void;
};

/**
 * Slides a side panel and its backdrop in and out. With reduced animations
 * (EN-11003) it jumps straight to the open or closed state instead. The
 * preference is read through a ref so flipping it inside an open panel does
 * not replay the entrance.
 */
export function usePanelSlide({
  open,
  rendered,
  panelRef,
  backdropRef,
  hidden,
  onClosed
}: PanelSlide) {
  const { reduceAnimations } = useAnimationPreferences();
  const latest = useRef({ reduceAnimations, hidden, onClosed });
  latest.current = { reduceAnimations, hidden, onClosed };

  useEffect(() => {
    const panel = panelRef.current;
    const backdrop = backdropRef.current;
    if (!rendered || !panel || !backdrop) return;
    const { reduceAnimations: reduce, hidden: offscreen, onClosed: closed } = latest.current;

    if (open) {
      // Clickable from the first frame, so a tap on the backdrop closes the panel.
      gsap.set(backdrop, { pointerEvents: 'auto' });
      if (reduce) {
        gsap.set(backdrop, { opacity: 1 });
        gsap.set(panel, { ...offscreen, x: '0%' });
        return;
      }
      // `overwrite` kills the other direction's tween, so a close during the
      // slide-in (or a reopen during the slide-out) never has two tweens
      // fighting over the panel, nor the old fade's onComplete unmounting it.
      gsap.to(backdrop, { opacity: 1, duration: 0.25, overwrite: true });
      gsap.fromTo(panel, offscreen, {
        x: '0%',
        duration: 0.4,
        ease: 'power3.out',
        overwrite: true
      });
      return;
    }

    const finish = () => {
      backdrop.style.pointerEvents = 'none';
      closed();
    };
    if (reduce) {
      gsap.set(panel, { x: offscreen.x });
      gsap.set(backdrop, { opacity: 0 });
      finish();
      return;
    }
    gsap.to(panel, { x: offscreen.x, duration: 0.3, ease: 'power2.in', overwrite: true });
    gsap.to(backdrop, { opacity: 0, duration: 0.25, overwrite: true, onComplete: finish });
  }, [open, rendered, panelRef, backdropRef]);
}
